import { useCallback, useState } from "react"
import { useAppSelector, useAppDispatch } from "../../application/states/hooks"
import { useLoadingService } from "../loading"
import { useEmailTestApi } from "../../infra"
import { executeAlert } from "../../application/states/toast-alert"

export const useEmailTestService = () => {
    const [isLoading, setIsLoading] = useState(false)
    const dispatch = useAppDispatch()
    const { startLoading, stopLoading } = useLoadingService()
    const { sendTestEmailApi } = useEmailTestApi()
    
    // Estados do Redux
    const { resaleConfig } = useAppSelector((state) => state.resaleConfig)
    const { selectedCompanyResaleConfig } = useAppSelector((state) => state.backofficeCompany)
    const { company } = useAppSelector((state) => state.company)

    const sendTestEmail = useCallback(async (testEmail: string, isBackoffice: boolean = false) => {
        try {
            setIsLoading(true)
            startLoading()

            // Determina qual configuração usar (backoffice ou resale config)
            const config = isBackoffice ? selectedCompanyResaleConfig.resaleConfig : resaleConfig
            
            if (!config) {
                throw new Error('Configuração de email não encontrada')
            }

            // Validações básicas
            if (!testEmail || !testEmail.includes('@')) {
                throw new Error('Email de teste inválido')
            }

            if (!config.emailProviderSettings.host || !config.emailProviderSettings.port) {
                throw new Error('Configurações de host e porta são obrigatórias')
            }

            if (!config.emailProviderSettings.user) {
                throw new Error('Email do provedor é obrigatório')
            }

            // Monta a configuração de email
            const emailConfig = {
                authType: config.emailProviderSettings.authType || 'simple',
                host: config.emailProviderSettings.host,
                port: Number(config.emailProviderSettings.port),
                user: config.emailProviderSettings.user,
                ...(config.emailProviderSettings.authType === 'simple' && {
                    password: config.emailProviderSettings.password
                }),
                ...(config.emailProviderSettings.authType === 'oauth2' && {
                    oauth2: config.emailProviderSettings.oauth2
                })
            }

            // Dados da empresa
            const companyName = config.name || 'Sua Empresa'
            const contactEmail = config.contact?.email || 'contato@empresa.com'
            const contactPhone = config.contact?.phone || '(11) 99999-9999'
            const primaryColor = config.theme?.colors?.primary?.main || '#006c95'

            const requestData = {
                testEmail,
                companyName,
                contactEmail,
                contactPhone,
                primaryColor,
                emailConfig
            }

            const response = await sendTestEmailApi(requestData)

            dispatch(executeAlert({
                type: 'success',
                message: `Email de teste enviado com sucesso para ${testEmail}!`
            }))

            return response

        } catch (error: any) {
            console.error('Erro ao enviar email de teste:', error)
            
            const errorMessage = error.response?.data?.error || error.message || 'Erro ao enviar email de teste'
            
            dispatch(executeAlert({
                type: 'error',
                message: errorMessage
            }))

            throw error
        } finally {
            setIsLoading(false)
            stopLoading()
        }
    }, [resaleConfig, selectedCompanyResaleConfig, sendTestEmailApi, startLoading, stopLoading])

    return {
        sendTestEmail,
        isLoading
    }
}
